import { useEffect } from "react";

/**
 * The attribute on <html> that says a Discover phone sheet has the screen.
 *
 * The host's site header answers to it (apps/majourneys `tailwind.css`): on a
 * phone it slides up out of view while the attribute is set and slides back
 * when it is gone, the same way `data-hub-compact` folds it on the hub pages.
 * One flag on the root rather than a context, because the header is not
 * inside the Discover tree, and rather than the header querying the DOM for
 * sheets.
 *
 * Set on every viewport: the phone shell is mounted on desktop too (only
 * `display: none`), so the host's CSS limits the effect to the phone
 * breakpoint, not this hook.
 */
export const SHEET_TAKEOVER_ATTR = "data-mj-sheet-takeover";

/**
 * Counted, not a plain on/off: one sheet unmounting while another mounts
 * (Explore to Liked, trip list to trip) must not drop the flag the newcomer
 * just set.
 */
let holders = 0;

function sync() {
  const root = document.documentElement;
  if (holders > 0) root.setAttribute(SHEET_TAKEOVER_ATTR, "");
  else root.removeAttribute(SHEET_TAKEOVER_ATTR);
}

/** Hide the site header on phones while `active` is true and this is mounted. */
export function useHeaderTakeover(active: boolean): void {
  useEffect(() => {
    if (!active) return undefined;
    holders += 1;
    sync();
    return () => {
      holders = Math.max(0, holders - 1);
      sync();
    };
  }, [active]);
}
